import React, { useContext, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import CallSplitOutlinedIcon from "@mui/icons-material/CallSplitOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { AuthContext } from "../../context/Auth/AuthContext";
import { analyzeDocumentSeparation, saveDocumentSeparation } from "../../api/documentSeparation";
import { showSuccess, showError } from "../../utils/toast";

// Review dialog for separating a multi-document PDF: shows each detected
// document (name, page range, matched request, category, action, status)
// and lets the user correct them. On confirm, documents with a request become
// normal documents linked to it (or are added to the request's existing
// document with the existing merge), and only then are those pages removed
// from the original PDF (earlier version kept). Documents without a request
// stay in the original PDF.

const STATUS = {
  matched: { label: "Matched", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  manual: { label: "Manual", className: "border-sky-200 bg-sky-50 text-sky-700" },
  needs_review: { label: "Needs review", className: "border-amber-200 bg-amber-50 text-amber-700" },
  stays: { label: "No request", className: "border-gray-200 bg-gray-50 text-gray-600" },
};

const fieldClass =
  "h-8 w-full rounded-md border bg-white px-2 text-xs text-gray-800 focus:border-[#1a6b7c] focus:outline-none";

// Page number box: wide enough for 3 digits, centred, no spinner arrows.
const pageInputClass =
  "h-8 w-12 rounded-md border bg-white px-1 text-center text-xs font-medium text-gray-900 focus:border-[#1a6b7c] focus:outline-none " +
  "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none";

// What happens to each row on save - mirrors the server's segmentAction():
// a request that already has a document reuses the existing merge (the pages
// are added to that document, as with Link Request) instead of creating a
// duplicate; pages already present, or with no mergeable target, stay in the
// original PDF.
const MOVES = ["create", "merge", "merge_new"];
const computeActions = (rows, requestById, pageDuplicates) => {
  const created = new Set();
  const out = {};
  [...rows]
    .sort((a, b) => a.page_from - b.page_from)
    .forEach((r) => {
      const rid = r.document_request_id;
      if (!rid) {
        out[r.key] = { type: "stays" };
        return;
      }
      const existing = requestById[rid]?.existing_document;
      if (existing) {
        if (existing.is_master) out[r.key] = { type: "in_original", existing };
        else if (!existing.is_pdf) out[r.key] = { type: "unsupported", existing };
        else {
          let allPresent = true;
          for (let p = r.page_from; p <= r.page_to; p++) {
            if (!((pageDuplicates || {})[p] || []).includes(rid)) {
              allPresent = false;
              break;
            }
          }
          out[r.key] = { type: allPresent ? "duplicate" : "merge", existing };
        }
        return;
      }
      if (created.has(rid)) {
        out[r.key] = { type: "merge_new" };
        return;
      }
      created.add(rid);
      out[r.key] = { type: "create" };
    });
  return out;
};

const ACTION_STYLE = {
  create: "border-emerald-200 bg-emerald-50 text-emerald-700",
  merge: "border-sky-200 bg-sky-50 text-sky-700",
  merge_new: "border-sky-200 bg-sky-50 text-sky-700",
  duplicate: "border-gray-200 bg-gray-50 text-gray-600",
  in_original: "border-gray-200 bg-gray-50 text-gray-600",
  unsupported: "border-amber-200 bg-amber-50 text-amber-700",
  stays: "border-gray-200 bg-gray-50 text-gray-600",
};

const actionText = (action) => {
  const ex = action.existing;
  switch (action.type) {
    case "create":
      return ["New document", "A new document will be created and linked to this request."];
    case "merge":
      return [
        "Add to existing",
        `Pages will be added to "${ex.document_name}".` +
          (ex.request_status === "Approved" ? " The request returns to Uploaded for re-approval." : ""),
      ];
    case "merge_new":
      return ["Add to new document", "Pages will be added to the new document above for this request."];
    case "duplicate":
      return ["Already present", `These pages are already in "${ex.document_name}" — they stay in the original.`];
    case "in_original":
      return ["Already linked", "The original PDF is this request's document — pages stay in it."];
    case "unsupported":
      return ["Cannot merge", `"${ex.document_name}" is not a PDF — pages stay in the original.`];
    default:
      return ["Stays in original", "No request selected — pages stay in the original."];
  }
};

const isPageNumber = (v, max) => Number.isInteger(v) && v >= 1 && v <= max;

// Per-row problems: { title, message, fields: Set("name" | "from" | "to") }.
const validateRows = (rows, pageCount) => {
  const issues = {};
  const add = (key, title, message, fields) => {
    if (!issues[key]) issues[key] = { title, messages: [], fields: new Set() };
    issues[key].messages.push(message);
    fields.forEach((f) => issues[key].fields.add(f));
  };

  rows.forEach((r) => {
    const fromOk = isPageNumber(r.page_from, pageCount);
    const toOk = isPageNumber(r.page_to, pageCount);
    if (!fromOk || !toOk) {
      add(r.key, "Invalid page number", `Pages must be whole numbers from 1 to ${pageCount}.`, [
        ...(fromOk ? [] : ["from"]),
        ...(toOk ? [] : ["to"]),
      ]);
    } else if (r.page_from > r.page_to) {
      add(r.key, "Invalid page range", "The first page can't be after the last page.", ["from", "to"]);
    }
    if (!String(r.document_name || "").trim()) {
      add(r.key, "Missing name", "Enter a document name.", ["name"]);
    }
  });

  // Overlaps between valid ranges - reported on both rows.
  const valid = rows.filter(
    (r) => isPageNumber(r.page_from, pageCount) && isPageNumber(r.page_to, pageCount) && r.page_from <= r.page_to
  );
  valid.forEach((a, i) => {
    valid.slice(i + 1).forEach((b) => {
      const from = Math.max(a.page_from, b.page_from);
      const to = Math.min(a.page_to, b.page_to);
      if (from > to) return;
      const pages = to > from ? `Pages ${from}–${to} are` : `Page ${from} is`;
      add(a.key, "Page range overlaps", `${pages} also assigned to "${b.document_name || "another document"}".`, ["from", "to"]);
      add(b.key, "Page range overlaps", `${pages} also assigned to "${a.document_name || "another document"}".`, ["from", "to"]);
    });
  });

  Object.values(issues).forEach((i) => {
    if (i.title === "Page range overlaps") i.messages.push("Please change the page range.");
  });

  return issues;
};

const DocumentSeparationDialog = ({ open, onClose, documentID, onSaved }) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);

  const load = async () => {
    setLoading(true);
    setError("");
    setData(null);
    setConfirming(false);
    const res = await analyzeDocumentSeparation({ token, documentId: documentID });
    if (res?.status === "success" && res.data) {
      setData(res.data);
      setRows(
        res.data.segments.map((s, i) => ({
          key: `${i}-${s.page_from}`,
          page_from: s.page_from,
          page_to: s.page_to,
          document_name: s.document_name,
          document_request_id: s.document_request_id || "",
          suggested_request_id: s.suggested_request_id || "",
          document_category: s.document_category || "",
          confidence: s.confidence,
          reason: s.reason,
          match_status: s.match_status,
        }))
      );
    } else {
      setError(res?.error || "Could not analyze this document.");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open && documentID) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documentID]);

  const requests = useMemo(() => data?.requests || [], [data]);
  const requestById = useMemo(() => Object.fromEntries(requests.map((r) => [r.id, r])), [requests]);
  const pageCount = data?.master?.page_count || 0;

  const update = (key, patch) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // Keep what the user types ("" while editing); validation reports bad values.
  const setPage = (key, field, value) => update(key, { [field]: value === "" ? "" : Number(value) });

  const changeRequest = (row, requestId) => {
    const req = requestById[requestId];
    update(row.key, {
      document_request_id: requestId,
      document_category: req ? req.category : "",
      document_name:
        req && (!row.document_name || row.document_name === requestById[row.document_request_id]?.name)
          ? req.name
          : row.document_name,
      match_status: !requestId ? "needs_review" : requestId === row.suggested_request_id ? "matched" : "manual",
    });
  };

  const addRow = () => {
    const last = rows[rows.length - 1];
    const start = Math.min(pageCount, (Number(last?.page_to) || 0) + 1);
    setRows((rs) => [
      ...rs,
      {
        key: `new-${Date.now()}`,
        page_from: start,
        page_to: start,
        document_name: `Pages ${start}`,
        document_request_id: "",
        suggested_request_id: "",
        document_category: "",
        confidence: 0,
        reason: "Added manually",
        match_status: "needs_review",
      },
    ]);
  };

  const issues = useMemo(() => validateRows(rows, pageCount), [rows, pageCount]);
  const hasIssues = Object.keys(issues).length > 0;

  const actions = useMemo(() => computeActions(rows, requestById, data?.page_duplicates), [rows, requestById, data]);
  // Rows whose pages leave the original PDF (new document or added to an existing one).
  const toSeparate = rows.filter((r) => MOVES.includes(actions[r.key]?.type));
  const newCount = rows.filter((r) => actions[r.key]?.type === "create").length;
  const addCount = toSeparate.length - newCount;
  const skippedCount = rows.filter((r) => ["duplicate", "in_original", "unsupported"].includes(actions[r.key]?.type)).length;
  const noRequestCount = rows.filter((r) => !r.document_request_id).length;

  // Pages that leave the original PDF vs. pages that stay; pages in no row.
  const { remainingCount, uncovered } = useMemo(() => {
    const moved = new Set();
    const covered = new Set();
    rows.forEach((r) => {
      if (!isPageNumber(r.page_from, pageCount) || !isPageNumber(r.page_to, pageCount)) return;
      for (let p = r.page_from; p <= r.page_to; p++) {
        covered.add(p);
        if (MOVES.includes(actions[r.key]?.type)) moved.add(p);
      }
    });
    const missing = [];
    for (let p = 1; p <= pageCount; p++) if (!covered.has(p)) missing.push(p);
    return { remainingCount: Math.max(0, pageCount - moved.size), uncovered: missing };
  }, [rows, actions, pageCount]);

  useEffect(() => setConfirming(false), [rows]);

  const canSeparate = !!data && !loading && !saving && !hasIssues && toSeparate.length > 0;

  const save = async () => {
    if (!canSeparate) return;
    setSaving(true);
    const res = await saveDocumentSeparation({
      token,
      documentId: documentID,
      sourceHash: data?.master?.source_hash,
      segments: [...rows]
        .sort((a, b) => a.page_from - b.page_from)
        .map((r) => ({
          page_from: Number(r.page_from),
          page_to: Number(r.page_to),
          document_name: String(r.document_name).trim(),
          document_request_id: r.document_request_id || null,
          document_category: r.document_request_id ? r.document_category || null : null,
          match_status: r.match_status,
          confidence: r.confidence,
        })),
    });
    setSaving(false);
    if (res?.status === "success") {
      showSuccess(res.message || "Documents separated");
      onClose();
      if (onSaved) onSaved(res.data);
    } else {
      setConfirming(false);
      showError(res?.error || "Could not separate the documents");
    }
  };

  const infoNotes = [
    uncovered.length > 0 && `Pages ${uncovered.join(", ")} are not in any row — they stay in the original PDF.`,
    noRequestCount > 0 &&
      `${noRequestCount} row${noRequestCount === 1 ? " has" : "s have"} no request — ${noRequestCount === 1 ? "its" : "their"} pages stay in the original PDF.`,
    skippedCount > 0 &&
      `${skippedCount} row${skippedCount === 1 ? " is" : "s are"} skipped (already present or can't be merged) — those pages stay in the original PDF.`,
  ].filter(Boolean);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { borderRadius: "12px" } }}>
      {/* Header */}
      <div className="flex items-center gap-2.5 border-b border-gray-200 px-4 py-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#e7f6fa] text-[#1a6b7c]">
          <CallSplitOutlinedIcon sx={{ fontSize: 18 }} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold text-gray-900">Separate Documents</h2>
          <p className="truncate text-xs text-gray-500">
            {data?.master
              ? `${data.master.document_name} · ${pageCount} page${pageCount === 1 ? "" : "s"}`
              : "Detect the documents inside this PDF and link them to the client's requests"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={saving}
          className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          aria-label="Close"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <div className="max-h-[68vh] overflow-y-auto px-4 py-3">
        {loading ? (
          <div className="flex flex-col items-center gap-2 py-12 text-center">
            <CircularProgress size={26} sx={{ color: "#1a6b7c" }} />
            <p className="text-sm text-gray-700">Analyzing pages…</p>
            <p className="max-w-md text-xs text-gray-500">
              Uses the document's OCR data. If the full document has not been OCR'd yet this runs first and can take a few
              minutes for long PDFs.
            </p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button
              type="button"
              onClick={load}
              className="h-7 rounded-md border border-gray-200 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              Try again
            </button>
          </div>
        ) : data ? (
          <div className="flex flex-col gap-2.5">
            {data.existing_splits?.length > 0 && (
              <div className="flex items-center gap-1.5 rounded-md border border-sky-100 bg-sky-50 px-2.5 py-1.5 text-xs text-sky-800">
                <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                <span>
                  {data.existing_splits.length} document{data.existing_splits.length === 1 ? " was" : "s were"} separated
                  from this PDF before; those pages are no longer in it.
                </span>
              </div>
            )}
            {requests.length === 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
                This client has no document requests, so there is nothing to link the pages to.
              </div>
            )}

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full min-w-[980px] table-fixed text-left text-xs">
                <colgroup>
                  <col className="w-[22%]" />
                  <col className="w-[118px]" />
                  <col />
                  <col className="w-[122px]" />
                  <col className="w-[200px]" />
                  <col className="w-[96px]" />
                  <col className="w-[36px]" />
                </colgroup>
                <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-2.5 py-2">Document Name</th>
                    <th className="px-2.5 py-2">Pages</th>
                    <th className="px-2.5 py-2">Matched Request</th>
                    <th className="px-2.5 py-2">Category</th>
                    <th className="px-2.5 py-2">Action</th>
                    <th className="px-2.5 py-2">Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const issue = issues[row.key];
                    const status = row.document_request_id
                      ? STATUS[row.match_status] || STATUS.manual
                      : row.suggested_request_id || row.match_status === "needs_review"
                        ? STATUS.needs_review
                        : STATUS.stays;
                    const suggested =
                      row.suggested_request_id && !row.document_request_id ? requestById[row.suggested_request_id] : null;
                    const action = actions[row.key] || { type: "stays" };
                    const [actionLabel, actionDetail] = actionText(action);
                    const bad = (field) => issue?.fields.has(field);
                    return (
                      <React.Fragment key={row.key}>
                        <tr
                          className={`border-t border-gray-100 align-top ${issue ? "bg-red-50/40 shadow-[inset_3px_0_0_#f87171]" : ""}`}
                        >
                          <td className="px-2.5 py-2">
                            <input
                              value={row.document_name}
                              onChange={(e) => update(row.key, { document_name: e.target.value })}
                              className={`${fieldClass} ${bad("name") ? "border-red-300" : "border-gray-300"}`}
                              aria-label="Document name"
                            />
                            {row.reason && (
                              <div className="mt-1 line-clamp-2 text-[11px] leading-snug text-gray-400" title={row.reason}>
                                {row.reason}
                              </div>
                            )}
                          </td>
                          <td className="px-2.5 py-2">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={pageCount}
                                value={row.page_from}
                                onChange={(e) => setPage(row.key, "page_from", e.target.value)}
                                className={`${pageInputClass} ${bad("from") ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                                aria-label="From page"
                              />
                              <span className="text-gray-400">–</span>
                              <input
                                type="number"
                                inputMode="numeric"
                                min={1}
                                max={pageCount}
                                value={row.page_to}
                                onChange={(e) => setPage(row.key, "page_to", e.target.value)}
                                className={`${pageInputClass} ${bad("to") ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                                aria-label="To page"
                              />
                            </div>
                          </td>
                          <td className="px-2.5 py-2">
                            <select
                              value={row.document_request_id}
                              onChange={(e) => changeRequest(row, e.target.value)}
                              className={`${fieldClass} truncate border-gray-300`}
                              title={
                                row.document_request_id
                                  ? (() => {
                                      const r = requestById[row.document_request_id];
                                      return r ? `${r.service ? `${r.service} - ` : ""}${r.name} (${r.status})` : "";
                                    })()
                                  : "No request"
                              }
                              aria-label="Matched request"
                            >
                              <option value="">— No request —</option>
                              {requests.map((r) => (
                                <option key={r.id} value={r.id}>
                                  {r.service ? `${r.service} - ${r.name}` : r.name}
                                  {r.status ? ` (${r.status})` : ""}
                                  {r.existing_document ? " · has document" : ""}
                                </option>
                              ))}
                            </select>
                            {suggested && (
                              <button
                                type="button"
                                onClick={() => changeRequest(row, suggested.id)}
                                className="mt-1 text-left text-[11px] text-[#1a6b7c] hover:underline"
                              >
                                Possible match: {suggested.name} — use it
                              </button>
                            )}
                          </td>
                          <td className="px-2.5 py-2">
                            <select
                              value={row.document_category}
                              onChange={(e) => update(row.key, { document_category: e.target.value })}
                              disabled={!row.document_request_id}
                              className={`${fieldClass} border-gray-300 disabled:bg-gray-50 disabled:text-gray-400`}
                              aria-label="Category"
                            >
                              <option value="">—</option>
                              {(data.categories || ["Required", "Recommended"]).map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2.5 py-2">
                            <span
                              className={`inline-block rounded border px-1.5 py-px text-[11px] font-semibold ${ACTION_STYLE[action.type]}`}
                            >
                              {actionLabel}
                            </span>
                            <div className="mt-1 text-[11px] leading-snug text-gray-500">{actionDetail}</div>
                          </td>
                          <td className="px-2.5 py-2">
                            <span className={`inline-block rounded border px-1.5 py-px text-[11px] font-semibold ${status.className}`}>
                              {status.label}
                            </span>
                            {row.confidence > 0 && (
                              <div className="mt-1 text-[11px] text-gray-500">{Math.round(row.confidence * 100)}% match</div>
                            )}
                          </td>
                          <td className="px-1 py-2 text-center">
                            <Tooltip title="Remove row" arrow>
                              <button
                                type="button"
                                onClick={() => setRows((rs) => rs.filter((r) => r.key !== row.key))}
                                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                aria-label="Remove row"
                              >
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                              </button>
                            </Tooltip>
                          </td>
                        </tr>
                        {issue && (
                          <tr className="bg-red-50/40 shadow-[inset_3px_0_0_#f87171]">
                            <td colSpan={7} className="px-2.5 pb-2 pt-0">
                              <div className="flex items-start gap-1.5 rounded-md border border-red-100 bg-red-50 px-2.5 py-1.5 text-[11px] text-red-700">
                                <WarningAmberOutlinedIcon sx={{ fontSize: 15, mt: "1px" }} />
                                <div>
                                  <span className="font-semibold">{issue.title}</span>
                                  {issue.messages.map((m) => (
                                    <div key={m}>{m}</div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              onClick={addRow}
              disabled={!pageCount}
              className="flex w-fit items-center gap-1 text-xs font-medium text-[#1a6b7c] hover:underline disabled:opacity-50"
            >
              <AddOutlinedIcon sx={{ fontSize: 15 }} /> Add document
            </button>

            {infoNotes.length > 0 && (
              <div className="flex items-start gap-1.5 rounded-md bg-gray-50 px-2.5 py-1.5 text-[11px] text-gray-600">
                <InfoOutlinedIcon sx={{ fontSize: 14, mt: "1px" }} className="text-gray-400" />
                <div>
                  {infoNotes.map((n) => (
                    <div key={n}>{n}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-end gap-1.5 border-t border-gray-200 px-4 py-2.5">
        {confirming ? (
          <p className="mr-auto text-xs text-gray-700">
            {newCount > 0 && (
              <>
                Create <b>{newCount}</b> new document{newCount === 1 ? "" : "s"}
                {addCount > 0 ? ", " : " "}
              </>
            )}
            {addCount > 0 && (
              <>
                add <b>{addCount}</b> to existing document{addCount === 1 ? "" : "s"}{" "}
              </>
            )}
            then{" "}
            {remainingCount > 0 ? (
              <>
                remove those pages from the original PDF (<b>{remainingCount}</b> page{remainingCount === 1 ? "" : "s"} stay; the
                previous version is kept).
              </>
            ) : (
              <>move the original PDF to the trash (every page is separated).</>
            )}
          </p>
        ) : (
          hasIssues && (
            <p className="mr-auto flex items-center gap-1 text-xs text-red-600">
              <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} /> Fix the highlighted rows to continue.
            </p>
          )
        )}
        <button
          type="button"
          onClick={confirming ? () => setConfirming(false) : onClose}
          disabled={saving}
          className="h-8 rounded-md border border-gray-200 bg-white px-3 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
        >
          {confirming ? "Back" : "Cancel"}
        </button>
        <button
          type="button"
          onClick={confirming ? save : () => setConfirming(true)}
          disabled={!canSeparate}
          className="h-8 rounded-md bg-[#1a6b7c] px-3.5 text-xs font-semibold text-white hover:bg-[#155e6e] disabled:cursor-not-allowed disabled:bg-[#1a6b7c]/40"
        >
          {saving
            ? "Separating…"
            : confirming
              ? "Confirm"
              : `Separate ${toSeparate.length} document${toSeparate.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </Dialog>
  );
};

export default DocumentSeparationDialog;
